import { Component, OnDestroy } from "@angular/core";
import { SavingEmitterService } from "./services/emitters/saving-emitter.service";
import { ErrorEmitterService } from "./services/emitters/error-emitter.service";
import { SplashScreenService } from "./services/generic/splash-screen.service";
import { ExperiencesService } from "./services/experience/experiences.service";
import { ExperienceCountService } from "./services/experience/experience-count.service";
import {
  style,
  state,
  animate,
  transition,
  trigger,
} from "@angular/animations";
import {
  Router,
  Event,
  NavigationStart,
  NavigationEnd,
  NavigationError,
} from "@angular/router";
import { Counter } from "@models/counter";
import { ReplaySubject } from "rxjs";
import { map, take, takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { OidcSecurityService } from "angular-auth-oidc-client";
@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  animations: [
    trigger("fadeInOut", [
      transition(":enter", [
        // :enter is alias to 'void => *'
        style({ opacity: 0 }),
        animate(500, style({ opacity: 1 })),
      ]),
      transition(":leave", [
        // :leave is alias to '* => void'
        animate(500, style({ opacity: 0 })),
      ]),
    ]),
    trigger("fade", [
      transition(":leave", [
        // :leave is alias to '* => void'
        animate(800, style({ opacity: 0 })),
      ]),
    ]),
    trigger("show", [
      transition(":enter", [
        // :enter is alias to 'void => *'
        style({ opacity: 0 }),
        animate(1000, style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class AppComponent implements OnDestroy {
  title = "Strowr";
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  public hideComponents: boolean;
  public savingBool: boolean = false;
  public errorBool: boolean = false;
  public profile: any;
  public isAuthorized: boolean = false;
  public innerWidth: number;
  public openMobile: boolean = false;
  public appMobile = false;
  public counter: Counter;
  constructor(
    public auth: OidcSecurityService,
    private translate: TranslateService,
    private eventEmitter: SavingEmitterService,
    private errorEmitter: ErrorEmitterService,
    private splashSvc: SplashScreenService,
    private experienceSvc: ExperiencesService,
    private experienceCountSvc: ExperienceCountService,
    private experienceCount: ExperienceCountService,
    private router: Router
  ) {
    this.translate.setDefaultLang("en");

    this.auth
      .checkAuth()
      .subscribe((loginResponse) => {
        this.isAuthorized = loginResponse.isAuthenticated;
        this.profile = loginResponse.userData;
      });

    this.router.events
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: Event) => {
        if (event instanceof NavigationEnd) {
          if (
            event.url.indexOf("404") > -1 ||
            event.urlAfterRedirects.indexOf("404") > -1
          ) {
            this.isAuthorized = true;
            this.hideComponents = true;
            this.errorBool = false;
          }
        }
      });
    this.innerWidth = window.innerWidth;
    this.splashSvc.splashScreen
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        this.isAuthorized = data;
        if (this.isAuthorized && window.location.href.indexOf("404") == -1) {
          this.experienceSvc
            .getExperienceCount()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
              (x) => {
                if (x.draftVersionsCount > 0) {
                  this.experienceCountSvc.sendCount(x);
                  // this.router.navigate(["drafts"]);
                }
                this.experienceCountSvc.sendCount(x);
              },
              (err) => {
                this.errorEmitter.sendError(true);
              }
            );
        }
      });

    this.eventEmitter.savingBool
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => (this.savingBool = data));

    let that = this;
    this.errorEmitter.errorBool
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        this.errorBool = data;
        if (this.errorBool) {
          setTimeout(function () {
            that.errorEmitter.sendError(false);
          }, 3000);
        }
      });
  }

  ngOnInit() {
    this.translateInit();
  }

  private translateInit() {
    let systemLang = localStorage.getItem("langForStrowBusiness");
    const defaultLang = "bg";
    const suportedLangs = ["bg", "en"];

    this.translate.addLangs(suportedLangs);
    this.translate.setDefaultLang(defaultLang);

    if (systemLang === null || systemLang === "undefined") {
      const browserLanguage = this.getBrowserLanguage(suportedLangs);

      if (browserLanguage) {
        systemLang = browserLanguage;
      } else {
        systemLang = defaultLang;
      }
    }

    this.translate.use(systemLang);

    localStorage.setItem("langForStrowBusiness", systemLang);
  }

  getBrowserLanguage(supportedLangs: Array<string>) {
    const browserLangs = window.navigator.languages || [
      window.navigator.language,
    ];

    if (
      typeof window === "undefined" ||
      typeof window.navigator === "undefined" ||
      typeof browserLangs === "undefined"
    ) {
      return undefined;
    }

    return this.matchSuportedLangsWithBrowserLangs(
      supportedLangs,
      browserLangs
    );
  }

  matchSuportedLangsWithBrowserLangs(
    supportedLangs: ReadonlyArray<string>,
    browserLangs: ReadonlyArray<string>
  ) {
    let lang;

    browserLangs.some((curruntBrowserLang) => {
      let tempCurruntBrowserLang = curruntBrowserLang;

      if (
        supportedLangs.some(
          (curruntSupportedLang) =>
            curruntSupportedLang === tempCurruntBrowserLang
        )
      ) {
        lang = tempCurruntBrowserLang;
        return true;
      }

      tempCurruntBrowserLang = this.trimLanguageRegion(tempCurruntBrowserLang);

      if (
        supportedLangs.some(
          (curruntSupportedLang) =>
            curruntSupportedLang === tempCurruntBrowserLang
        )
      ) {
        lang = tempCurruntBrowserLang;
        return true;
      }
    });

    return lang;
  }

  trimLanguageRegion(language: string) {
    if (language.indexOf("-") !== -1) {
      language = language.split("-")[0];
    }
    if (language.indexOf("_") !== -1) {
      language = language.split("_")[0];
    }

    return language;
  }

  public mobileToggle() {
    this.openMobile = !this.openMobile;
    this.appMobile = !this.appMobile;
  }

  public appBackgroundChange() {
    this.appMobile = !this.appMobile;
    this.openMobile = !this.openMobile;
  }

  public navigationNotify() {
    this.appMobile = !this.appMobile;
    this.openMobile = !this.openMobile;
  }

  public onResize(event) {
    this.innerWidth = window.innerWidth;

    if (1025 >= this.innerWidth) {
      this.appMobile = false;
      this.openMobile = false;
    }
  }

  public async logOut() {
    await this.auth.logoffAndRevokeTokens().toPromise();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
